import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  getProjects,
  createChangeRequest,
  getChangeRequests,
  uploadFile,
  updateProjectLifecycle,
  deleteProject,
  updateProjectNotes,
  API_BASE,
} from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Loader2, Repeat, Megaphone, Globe, Search as SearchIcon, Ban, Upload,
  X as XIcon, FileText, Send, ListChecks, Clock, Archive, Eye, MessageSquare, Trash2,
  Pencil, StickyNote,
} from "lucide-react";
import CommentThread from "../components/CommentThread";
import AddMigratedOrderDialog from "../components/AddMigratedOrderDialog";

const PRODUCT_LABELS = {
  marketing: "Marketing",
  web_design: "Web Design",
  seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID/Max",
  digital_marketing: "Digital Marketing",
  ai_tools: "AI Tools",
};

const REQUEST_TYPES = [
  { id: "marketing", label: "Marketing Change", icon: Megaphone, hint: "For active marketing campaigns", color: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100" },
  { id: "website", label: "Website Change", icon: Globe, hint: "Update site, monthly content change", color: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" },
  { id: "seo_aeo", label: "SEO/AEO Update", icon: SearchIcon, hint: "Content management updates", color: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" },
  { id: "cancellation", label: "Cancellation", icon: Ban, hint: "Stop service (set effective date)", color: "bg-zinc-50 text-zinc-700 border-zinc-200 hover:bg-zinc-100" },
];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function MonthlyActiveClientsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Change request form state
  const [formOpen, setFormOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [requestType, setRequestType] = useState("");
  const [comment, setComment] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [migratedDialogOpen, setMigratedDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Change request detail (with thread)
  const [crDetailOpen, setCrDetailOpen] = useState(false);
  const [activeCr, setActiveCr] = useState(null);

  // Edit notes dialog state
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesProject, setNotesProject] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  const openNotes = (project) => {
    setNotesProject(project);
    setNotesValue(project.notes || "");
    setNotesOpen(true);
  };

  const saveNotes = async () => {
    if (!notesProject) return;
    setNotesSaving(true);
    try {
      await updateProjectNotes(notesProject.id, notesValue);
      toast.success("Notes updated");
      setNotesOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update notes");
    } finally {
      setNotesSaving(false);
    }
  };

  const isAdmin = ["super_admin", "manager"].includes(user?.role);

  const fetchAll = async () => {
    try {
      const [projRes, crRes] = await Promise.all([getProjects(), getChangeRequests()]);
      setProjects(projRes.data.filter((p) => p.lifecycle_stage === "monthly_active"));
      setChangeRequests(crRes.data || []);
    } catch (e) {
      console.error("Failed to load monthly active clients:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openForm = (project) => {
    setActiveProject(project);
    setRequestType("");
    setComment("");
    setEffectiveDate("");
    setFiles([]);
    setFormOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(file);
      setFiles((prev) => [...prev, data]);
    } catch { toast.error("File upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removeFile = (id) => setFiles((p) => p.filter((f) => f.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requestType) { toast.error("Pick a request type"); return; }
    if (requestType === "cancellation" && !effectiveDate) { toast.error("Effective date required for cancellations"); return; }
    setSubmitting(true);
    try {
      await createChangeRequest({
        client_id: activeProject.client_id,
        project_id: activeProject.id,
        request_type: requestType,
        comment,
        effective_date: effectiveDate || null,
        file_ids: files.map((f) => f.id),
      });
      toast.success("Change request submitted!");
      setFormOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const handleArchive = async (projectId) => {
    try {
      await updateProjectLifecycle(projectId, "archived");
      toast.success("Moved to Archived Clients");
      fetchAll();
    } catch { toast.error("Failed to archive"); }
  };

  const handleDeleteOrder = async (projectId, clientLabel) => {
    if (!window.confirm(`Permanently delete this order for ${clientLabel || "this client"}? Related tasks and comments will also be removed. This cannot be undone.`)) return;
    try {
      await deleteProject(projectId);
      toast.success("Order deleted");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const getProductLabel = (p) => (p.product_types || [p.product_type]).map((t) => PRODUCT_LABELS[t] || t).join(" + ");

  const filtered = projects.filter((p) =>
    p.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.client_company?.toLowerCase().includes(search.toLowerCase()) ||
    getProductLabel(p).toLowerCase().includes(search.toLowerCase())
  );

  // Group by client
  const grouped = {};
  filtered.forEach((p) => {
    const key = p.client_name || "Unknown";
    if (!grouped[key]) grouped[key] = { client_company: p.client_company, agency_name: p.agency_name || "", projects: [] };
    grouped[key].projects.push(p);
  });

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="monthly-active-clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Monthly Active Clients</h1>
          <p className="text-muted-foreground mt-1">Clients with ongoing recurring monthly work</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {user?.role === "super_admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMigratedDialogOpen(true)}
              className="border-fuchsia-300 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-800"
              data-testid="add-migrated-order-btn"
              title="Add one existing client (with their live order) into Monthly Active — skips onboarding"
            >
              <Pencil className="h-4 w-4 mr-1.5" />
              Add Monthly Active Client
            </Button>
          )}
          {changeRequests.length > 0 && (
            <Badge variant="outline" className="px-3 py-1.5 gap-1.5">
              <ListChecks className="h-3.5 w-3.5" />
              {changeRequests.filter((cr) => cr.status === "open").length} open change requests
            </Badge>
          )}
        </div>
      </div>

      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by client name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-monthly-clients" />
      </div>

      {filtered.length === 0 ? (
        <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
          <Repeat className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium">No monthly active clients yet</h3>
          <p className="text-muted-foreground text-sm text-center max-w-md mt-1">
            Promote a client from "New Client Onboarding" once their initial setup is complete and they're on a recurring monthly engagement.
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([clientName, group]) => {
            const clientCRs = changeRequests.filter((cr) => group.projects.some((p) => p.client_id === cr.client_id));
            return (
              <Card key={clientName} className="border">
                <CardHeader className="pb-3">
                  <CardTitle className="font-outfit text-lg flex flex-wrap items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-semibold">
                      {clientName.charAt(0).toUpperCase()}
                    </div>
                    <span>{clientName}</span>
                    {group.agency_name && <span className="text-sm font-normal text-muted-foreground">/ {group.agency_name}</span>}
                    {group.client_company && <span className="text-xs font-normal text-muted-foreground">({group.client_company})</span>}
                    <Badge variant="outline" className="ml-auto bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Repeat className="h-3 w-3 mr-1" /> Monthly Active
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Previously ordered products */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Active Products</p>
                    <div className="flex flex-wrap gap-1.5">
                      {group.projects.map((p) => (
                        <Link key={p.id} to={`/orders/${p.id}`}>
                          <Badge variant="outline" className={`text-xs cursor-pointer hover:bg-primary/10 ${p.migrated ? "border-amber-300 bg-amber-50" : ""}`}>
                            {getProductLabel(p)}
                            {p.migrated && <span className="ml-1 text-[9px] uppercase font-bold text-amber-700">migrated</span>}
                          </Badge>
                        </Link>
                      ))}
                    </div>
                    {group.projects[0]?.sub_products?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Sub-products: {group.projects[0].sub_products.join(", ")}
                      </p>
                    )}
                  </div>

                  {/* Notes (preserved from bulk import / manual edits) */}
                  {group.projects[0]?.notes && (
                    <div className="rounded-md border bg-amber-50/40 border-amber-200 p-3" data-testid={`monthly-notes-${clientName}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs uppercase tracking-wide text-amber-800 flex items-center gap-1.5">
                          <StickyNote className="h-3.5 w-3.5" /> Notes
                        </p>
                        {isAdmin && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => openNotes(group.projects[0])}
                            data-testid={`edit-notes-btn-${clientName}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">
                        {group.projects[0].notes}
                      </p>
                      {group.projects[0].notes_updated_at && (
                        <p className="text-[11px] text-amber-700/80 mt-2" data-testid={`notes-byline-${clientName}`}>
                          Updated{group.projects[0].notes_updated_by_name ? ` by ${group.projects[0].notes_updated_by_name}` : ""} on {formatDate(group.projects[0].notes_updated_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Recent change requests for this client */}
                  {clientCRs.length > 0 && (
                    <div className="bg-zinc-50/50 rounded-lg border p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Recent Change Requests</p>
                      <div className="space-y-1.5">
                        {clientCRs.slice(0, 3).map((cr) => {
                          const rt = REQUEST_TYPES.find((r) => r.id === cr.request_type);
                          const Icon = rt?.icon || FileText;
                          return (
                            <button
                              key={cr.id}
                              type="button"
                              onClick={() => { setActiveCr(cr); setCrDetailOpen(true); }}
                              className="w-full flex items-center justify-between text-xs p-1.5 rounded hover:bg-zinc-100 transition-colors"
                              data-testid={`open-cr-${cr.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">{rt?.label || cr.request_type}</span>
                                <span className="text-muted-foreground">— {formatDate(cr.created_at)}</span>
                                <MessageSquare className="h-3 w-3 text-muted-foreground" />
                              </div>
                              <Badge className={cr.status === "completed" ? "bg-green-100 text-green-800" : cr.status === "in_progress" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"} variant="secondary">
                                {cr.status.replace("_", " ")}
                              </Badge>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={() => openForm(group.projects[0])} data-testid={`change-request-btn-${clientName}`}>
                      <Send className="mr-2 h-4 w-4" /> Submit Change/Update Request
                    </Button>
                    {isAdmin && !group.projects[0]?.notes && (
                      <Button
                        variant="outline"
                        onClick={() => openNotes(group.projects[0])}
                        data-testid={`add-notes-btn-${clientName}`}
                      >
                        <StickyNote className="mr-2 h-4 w-4" /> Add Notes
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="outline" onClick={() => handleArchive(group.projects[0].id)} data-testid={`archive-monthly-${clientName}`}>
                        <Archive className="mr-2 h-4 w-4" /> Move to Archived
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="outline"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                        onClick={() => handleDeleteOrder(group.projects[0].id, clientName)}
                        data-testid={`delete-monthly-${clientName}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </Button>
                    )}
                    <Link to={`/orders/${group.projects[0].id}`}>
                      <Button variant="ghost"><Eye className="mr-2 h-4 w-4" /> View Order</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Change Request Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="change-request-dialog">
          <DialogHeader>
            <DialogTitle>Client Change/Update Request</DialogTitle>
            <DialogDescription>
              {activeProject?.client_name}
              {activeProject?.agency_name ? ` / ${activeProject.agency_name}` : ""}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Pre-filled summary */}
            {activeProject && (
              <div className="bg-zinc-50/60 rounded-lg border p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="font-medium">{activeProject.client_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Previously Ordered</span><span className="font-medium">{getProductLabel(activeProject)}</span></div>
                {activeProject.sub_products?.length > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Sub-products</span><span className="font-medium text-right">{activeProject.sub_products.join(", ")}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Original Order Date</span><span className="font-medium">{formatDate(activeProject.created_at)}</span></div>
              </div>
            )}

            {/* Request type buttons */}
            <div>
              <Label className="text-sm mb-2 block">What kind of change?</Label>
              <div className="grid grid-cols-2 gap-2">
                {REQUEST_TYPES.map((rt) => {
                  const active = requestType === rt.id;
                  return (
                    <button
                      key={rt.id}
                      type="button"
                      onClick={() => setRequestType(rt.id)}
                      data-testid={`request-type-${rt.id}`}
                      className={`flex items-start gap-2 p-3 rounded-lg border text-left transition-all ${active ? "ring-2 ring-primary border-primary " + rt.color : rt.color}`}
                    >
                      <rt.icon className="h-5 w-5 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{rt.label}</div>
                        <div className="text-xs opacity-80">{rt.hint}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Effective date for cancellations */}
            {requestType === "cancellation" && (
              <div>
                <Label className="text-sm flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />Effective Date</Label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="cancellation-effective-date"
                />
              </div>
            )}

            {/* Comment */}
            <div>
              <Label className="text-sm">Tell Us What to Change</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe the change in detail. What's working, what isn't, what's new..."
                rows={4}
                className="mt-1"
                data-testid="change-request-comment"
              />
            </div>

            {/* File upload */}
            <div>
              <Label className="text-sm">Attach Files (optional)</Label>
              <div className="mt-1 space-y-2">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-2 rounded border bg-zinc-50">
                    <span className="text-sm flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{f.filename}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(f.id)}>
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="cr-upload-btn">
                  {uploading ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Uploading...</> : <><Upload className="mr-2 h-3 w-3" />Upload File</>}
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !requestType} data-testid="submit-change-request">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : <><Send className="mr-2 h-4 w-4" />Submit Request</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Request Detail Dialog (with conversation thread) */}
      <Dialog open={crDetailOpen} onOpenChange={setCrDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cr-detail-dialog">
          {activeCr && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const rt = REQUEST_TYPES.find((r) => r.id === activeCr.request_type);
                    const Icon = rt?.icon || FileText;
                    return <><Icon className="h-5 w-5" />{rt?.label || activeCr.request_type}</>;
                  })()}
                </DialogTitle>
                <DialogDescription>
                  {activeCr.client_name} • Submitted {formatDate(activeCr.created_at)} by {activeCr.submitted_by_name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Status + effective date */}
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <Badge className={activeCr.status === "completed" ? "bg-green-100 text-green-800" : activeCr.status === "in_progress" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}>
                    {activeCr.status.replace("_", " ")}
                  </Badge>
                  {activeCr.effective_date && (
                    <span className="text-muted-foreground"><Clock className="inline h-3.5 w-3.5 mr-1" />Effective {formatDate(activeCr.effective_date)}</span>
                  )}
                </div>

                {/* Original request */}
                {activeCr.comment && (
                  <div className="bg-zinc-50/60 rounded-lg border p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Original Request</p>
                    <p className="text-sm whitespace-pre-wrap">{activeCr.comment}</p>
                  </div>
                )}

                {/* Initial attachments */}
                {activeCr.file_ids?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Attachments</p>
                    <div className="flex flex-wrap gap-1.5">
                      {activeCr.file_ids.map((fid) => (
                        <a
                          key={fid}
                          href={`${API_BASE}/api/files/${fid}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline bg-primary/5 px-2 py-1 rounded"
                        >
                          <FileText className="h-3 w-3" /> File
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Thread */}
                <CommentThread
                  threadType="change_request"
                  threadId={activeCr.id}
                  title="Conversation"
                  description="Permanent record. The other party gets emailed when you post."
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-xl" data-testid="edit-notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" /> Edit Notes
            </DialogTitle>
            <DialogDescription>
              {notesProject?.client_name}
              {notesProject?.agency_name ? ` / ${notesProject.agency_name}` : ""}
              {" — "}
              These notes are visible to anyone with access to this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              rows={10}
              placeholder="Add context, clarify ambiguous imported data, leave hand-off notes for the team..."
              className="font-mono text-sm"
              data-testid="edit-notes-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNotesOpen(false)} disabled={notesSaving}>
                Cancel
              </Button>
              <Button onClick={saveNotes} disabled={notesSaving} data-testid="save-notes-btn">
                {notesSaving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  "Save Notes"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Super-admin migration action: backfill live orders that pre-date the portal */}
      <AddMigratedOrderDialog
        open={migratedDialogOpen}
        onOpenChange={setMigratedDialogOpen}
        onCreated={() => fetchAll()}
      />
    </div>
  );
}
